import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { CreditCard, CheckCircle, AlertCircle, ExternalLink, RefreshCw } from 'lucide-react'

function PaymentModeToggle({ school }) {
  const [mode, setMode] = useState(school.payment_mode || 'upfront')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleChange = async (newMode) => {
    setMode(newMode)
    setSaving(true)
    setSaved(false)
    await supabase.from('schools').update({ payment_mode: newMode }).eq('id', school.id)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const opts = [
    { value: 'upfront', label: 'Charge upfront', desc: 'Customer pays when booking' },
    { value: 'authorize', label: 'Authorize only', desc: 'Card held, you charge day-of' },
  ]

  return (
    <div style={{ background: 'white', border: '1.5px solid rgba(11,30,61,0.1)', borderRadius: 16, padding: '1.25rem 1.5rem', marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-dark)' }}>When to charge customers</div>
        {saving && <span style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>Saving...</span>}
        {saved && <span style={{ fontSize: '0.75rem', color: 'var(--teal)', fontWeight: 600 }}>✓ Saved</span>}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {opts.map(opt => (
          <button
            key={opt.value}
            onClick={() => handleChange(opt.value)}
            style={{
              flex: 1, padding: '0.875rem 1rem', borderRadius: 10, cursor: 'pointer',
              border: `2px solid ${mode === opt.value ? 'var(--navy)' : 'rgba(11,30,61,0.12)'}`,
              background: mode === opt.value ? 'rgba(11,30,61,0.05)' : 'white',
              textAlign: 'left', fontFamily: 'DM Sans, sans-serif',
              transition: 'all 0.15s',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: '0.85rem', color: mode === opt.value ? 'var(--navy)' : 'var(--text-dark)', marginBottom: 3 }}>{opt.label}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>{opt.desc}</div>
          </button>
        ))}
      </div>
      {mode === 'authorize' && (
        <div style={{ marginTop: 10, padding: '0.625rem 0.875rem', background: 'rgba(212,168,75,0.08)', border: '1px solid rgba(212,168,75,0.25)', borderRadius: 8, fontSize: '0.78rem', color: '#856a10' }}>
          Cards are authorized at booking. You'll see a "Charge Card" button on each lesson — click it when you're ready to collect payment (up to 7 days from booking).
        </div>
      )}
    </div>
  )
}

export default function AdminStripeConnect() {
  const { user } = useAuth()
  const [school, setSchool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user) return
    loadSchool()
  }, [user])

  const loadSchool = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('school_id')
      .eq('id', user.id)
      .single()

    if (!profile?.school_id) { setLoading(false); return }

    const { data: schoolData } = await supabase
      .from('schools')
      .select('id, name, stripe_account_id, charges_enabled, payment_mode')
      .eq('id', profile.school_id)
      .single()

    setSchool(schoolData)
    setLoading(false)
  }

  const handleConnect = async () => {
    setConnecting(true)
    setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/api/stripe/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ schoolId: school.id, adminEmail: user.email }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to start Stripe setup')
      // Redirect to Stripe hosted onboarding
      window.location.href = data.url
    } catch (err) {
      setError(err.message)
      setConnecting(false)
    }
  }

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div>
  )

  const isConnected = school?.charges_enabled
  const hasStarted = !!school?.stripe_account_id

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 4 }}>
          Payment Setup
        </h2>
        <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>
          Connect your bank account so customers can pay for lessons directly through WaveSync.
        </p>
      </div>

      {/* Status card */}
      <div style={{
        background: 'white',
        border: `1.5px solid ${isConnected ? 'rgba(14,170,164,0.3)' : 'rgba(212,168,75,0.35)'}`,
        borderRadius: 16,
        padding: '1.5rem',
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1.25rem',
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
          background: isConnected ? 'rgba(14,170,164,0.1)' : 'rgba(212,168,75,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isConnected
            ? <CheckCircle size={26} color="var(--teal)" />
            : <AlertCircle size={26} color="#a07a1a" />
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--text-dark)', marginBottom: 3 }}>
            {isConnected ? 'Payments Connected' : hasStarted ? 'Setup Incomplete' : 'Not Connected'}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-light)', lineHeight: 1.5 }}>
            {isConnected
              ? 'Your bank account is linked. Customers can now pay for lessons and funds will be deposited into your account.'
              : hasStarted
              ? 'You started the setup but didn\'t finish. Click below to continue where you left off.'
              : 'Connect your bank account to start accepting lesson payments from customers.'
            }
          </div>
        </div>
        <div style={{
          padding: '5px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
          background: isConnected ? 'rgba(14,170,164,0.1)' : 'rgba(212,168,75,0.15)',
          color: isConnected ? 'var(--teal)' : '#a07a1a',
        }}>
          {isConnected ? 'Active' : 'Pending'}
        </div>
      </div>

      {/* Payment mode toggle — only when connected */}
      {isConnected && <PaymentModeToggle school={school} />}

      {/* Error message */}
      {error && (
        <div style={{ padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem', background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.25)', color: 'var(--coral)', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}

      {/* Action button */}
      {!isConnected && (
        <button
          onClick={handleConnect}
          disabled={connecting}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: connecting ? 'rgba(11,30,61,0.4)' : 'var(--navy)',
            color: 'white', border: 'none', borderRadius: 10,
            padding: '13px 28px', fontSize: '0.9rem', fontWeight: 700,
            cursor: connecting ? 'not-allowed' : 'pointer',
            fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s',
          }}
        >
          {connecting
            ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Connecting...</>
            : hasStarted
            ? <><RefreshCw size={16} /> Continue Setup</>
            : <><CreditCard size={16} /> Connect Bank Account</>
          }
        </button>
      )}

      {/* Info box */}
      <div style={{
        marginTop: '1.5rem', padding: '1rem 1.25rem',
        background: 'rgba(11,30,61,0.04)',
        border: '1px solid rgba(11,30,61,0.08)',
        borderRadius: 12,
      }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 6 }}>How it works</div>
        <ul style={{ margin: 0, padding: '0 0 0 1.1rem', fontSize: '0.8rem', color: 'var(--text-light)', lineHeight: 1.9 }}>
          <li>Customers pay through WaveSync when booking lessons</li>
          <li>100% of the lesson price goes directly to your bank account</li>
          <li>Stripe charges their standard processing fee of 2.9% + 30¢ per transaction</li>
          <li>Powered by Stripe — bank-level security, no card data touches WaveSync servers</li>
        </ul>
      </div>
    </div>
  )
}
